import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface DeviceLldpNeighbor {
  connectionId: string;
  localPort: string | null;
  localPortDescription: string | null;
  neighborDeviceId: string;
  neighborName: string | null;
  neighborSystemDescription: string | null;
  neighborChassisId: string | null;
  neighborManagementIp: string | null;
  remotePort: string | null;
  remotePortDescription: string | null;
  discoveredAt: string;
  lastSeenAt: string;
  confidenceScore: number;
}

// Known LLDP source values plus a fall-through for forward compat.
// `string & {}` prevents TS from collapsing the union to plain string
// so the literals still autocomplete while leaving room for new sources
// to round-trip through the type.
export type DeviceLldpSource = 'snmp' | 'cli_lldpctl' | (string & {});

export interface DeviceLldpStatus {
  source: DeviceLldpSource;
  lastNeighborSeenAt: string | null;
  lldpEnabled: boolean;
}

export interface DeviceLldpResponse {
  deviceId: string;
  status: DeviceLldpStatus;
  neighbors: DeviceLldpNeighbor[];
}

export function useDeviceLldp(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceLldpResponse>({
    queryKey: ['deviceLldp', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<DeviceLldpResponse>(
        `${config.backendApi}/api/devices/${deviceId}/lldp-neighbors`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
  });
}
